import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

/**
 * ImageThumb — shared clickable product-image thumbnail with lightbox.
 * ---------------------------------------------------------------------
 * Consistent visual + interaction layer across every module that displays
 * product images (Procurement, Inbound, Orders, Inventory, Picklist,
 * Mapping, Catalog, Warehouse, etc.).
 *
 *   • 64x64 thumbnail by default (`size` prop overrides)
 *   • Click → full-screen lightbox
 *   • Zoom: buttons (+ / -) + mouse wheel + pinch (mobile)
 *   • Pan by dragging when zoomed in
 *   • Close: ESC / backdrop click / X button
 */

function Lightbox({ src, alt, onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => setScale((s) => Math.min(6, +(s + 0.4).toFixed(2))), []);
  const zoomOut = useCallback(() =>
    setScale((s) => {
      const n = Math.max(1, +(s - 0.4).toFixed(2));
      if (n === 1) setOffset({ x: 0, y: 0 });
      return n;
    }),
  []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, zoomIn, zoomOut, reset]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.2 : -0.2;
    setScale((s) => {
      const n = Math.max(1, Math.min(6, +(s + delta).toFixed(2)));
      if (n === 1) setOffset({ x: 0, y: 0 });
      return n;
    });
  }, []);

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    dragRef.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setOffset({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const onMouseUp = () => { dragRef.current = null; };

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { dist: Math.hypot(dx, dy), scale };
    } else if (e.touches.length === 1 && scale > 1) {
      dragRef.current = {
        x: e.touches[0].clientX - offset.x,
        y: e.touches[0].clientY - offset.y,
      };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const next = Math.max(1, Math.min(6, +(pinchRef.current.scale * (dist / pinchRef.current.dist)).toFixed(2)));
      setScale(next);
      if (next === 1) setOffset({ x: 0, y: 0 });
    } else if (e.touches.length === 1 && dragRef.current) {
      setOffset({
        x: e.touches[0].clientX - dragRef.current.x,
        y: e.touches[0].clientY - dragRef.current.y,
      });
    }
  };
  const onTouchEnd = () => {
    dragRef.current = null;
    pinchRef.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center"
      onClick={onClose}
      onWheel={onWheel}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      data-testid="image-lightbox"
    >
      <div
        className="absolute top-3 right-3 flex items-center gap-2 z-10"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white transition"
          onClick={zoomOut}
          title="Zoom out (-)"
          data-testid="lightbox-zoom-out"
        >
          <ZoomOut size={18} />
        </button>
        <span className="text-white/80 text-[12px] font-mono w-12 text-center select-none">
          {Math.round(scale * 100)}%
        </span>
        <button
          className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white transition"
          onClick={zoomIn}
          title="Zoom in (+)"
          data-testid="lightbox-zoom-in"
        >
          <ZoomIn size={18} />
        </button>
        <button
          className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white transition"
          onClick={reset}
          title="Reset (0)"
          data-testid="lightbox-reset"
        >
          <RotateCcw size={18} />
        </button>
        <button
          className="p-2 rounded-sm bg-white/10 hover:bg-white/20 text-white transition"
          onClick={onClose}
          title="Close (Esc)"
          data-testid="lightbox-close"
        >
          <X size={18} />
        </button>
      </div>
      <div
        className="max-w-[95vw] max-h-[92vh] overflow-hidden select-none"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ cursor: scale > 1 ? (dragRef.current ? "grabbing" : "grab") : "default" }}
      >
        <img
          src={src}
          alt={alt || ""}
          referrerPolicy="no-referrer"
          draggable={false}
          className="max-w-[95vw] max-h-[92vh] object-contain rounded transition-transform duration-75"
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
            transformOrigin: "center center",
          }}
        />
      </div>
    </div>
  );
}

export default function ImageThumb({
  src,
  alt = "",
  size = 64,
  className = "",
  fit = "cover",
  testId,
  placeholder = "—",
}) {
  const [open, setOpen] = useState(false);
  if (!src) return <span className="text-slate-400">{placeholder}</span>;
  const dim = { width: size, height: size };
  return (
    <>
      <img
        src={src}
        alt={alt}
        referrerPolicy="no-referrer"
        loading="lazy"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`object-${fit} border border-slate-200 rounded-sm cursor-zoom-in bg-slate-50 hover:border-slate-400 transition ${className}`}
        style={dim}
        data-testid={testId || "image-thumb"}
      />
      {open && <Lightbox src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
}

export { Lightbox };
